import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Inbox, Loader2, Clock, Check, X } from 'lucide-react';

/**
 * Patient requests (ADR 0008) — patients who asked THIS office to do their
 * paperwork and already paid for it. Nothing attaches on its own (decision 5):
 * a staff member accepts, after confirming this is their patient, because a
 * patient who mistyped the address would otherwise send a case to a stranger.
 */
export default function PatientRequests() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [accepting, setAccepting] = useState(null);
  const [declining, setDeclining] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/practice/patient-requests')
      .then((r) => setItems(r.data))
      .catch((err) => { setItems([]); toast.error(errText(err)); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/practice/patient-requests/${accepting.id}/accept`, { confirm_our_patient: true });
      toast.success('Accepted. The patient has been sent their secure form.');
      setAccepting(null);
      navigate(`/nurse/submission/${r.data.submission_id}`);
    } catch (err) {
      toast.error(errText(err));
      load();
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/practice/patient-requests/${declining.id}/decline`, { reason });
      toast.success('Declined. The patient was told and their card was not charged.');
      setDeclining(null);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto space-y-4">
      <div>
        <h1 className="text-base lg:text-lg font-semibold tracking-tight">Patient requests</h1>
        <p className="text-sm text-muted-foreground">
          Patients who asked your office to complete their leave paperwork. They have already paid, so an accepted
          request costs your practice nothing. Requests close on their own after 6 days.
        </p>
      </div>

      {items === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="pd-requests-empty">
          <Inbox className="h-6 w-6 mx-auto mb-2" /> No patient requests waiting.
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <Card key={it.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`pd-request-${it.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{it.patient_name}</p>
                <p className="text-xs text-muted-foreground truncate">Asked for {it.doctor_name} · sent to {it.office_email}</p>
                {it.days_left != null && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <Clock className="h-3 w-3" /> Closes in {it.days_left} day(s)
                  </p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => { setDeclining(it); setReason(''); }} data-testid={`pd-decline-${it.id}`}>
                  <X className="h-4 w-4 mr-1" /> Decline
                </Button>
                <Button size="sm" onClick={() => { setAccepting(it); setConfirmed(false); }} data-testid={`pd-accept-${it.id}`}>
                  <Check className="h-4 w-4 mr-1" /> Accept
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!accepting} onOpenChange={(o) => !o && setAccepting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept {accepting?.patient_name}?</DialogTitle>
            <DialogDescription>
              Accepting charges the patient’s card and creates a case in your queue. The patient then fills in their
              leave details on their secure form.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(v === true)} data-testid="pd-confirm-our-patient" />
            <span>I confirm this is our patient and {accepting?.doctor_name} is at our practice.</span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccepting(null)}>Cancel</Button>
            <Button onClick={accept} disabled={!confirmed || busy} data-testid="pd-accept-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Accept request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!declining} onOpenChange={(o) => !o && setDeclining(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline this request?</DialogTitle>
            <DialogDescription>
              The patient’s card hold is released and they are told the office did not take the request.
              They are never charged.
            </DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Optional note for your records (not sent to the patient)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclining(null)}>Cancel</Button>
            <Button variant="destructive" onClick={decline} disabled={busy} data-testid="pd-decline-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
